import React from "react";

import { IHouse, IMatch } from "interfaces/intarfaces";
import styled from "styled-components/macro";
import { RoomPhotosCarousel as RoomPhotosCarouselOriginal } from "../../Room/RoomPhotosCarousel";
import { PhotoPlaceholder } from "../../Room/RoomCard/styles";
import {
  Chevron,
  Delimiter,
  FeaturesBlock,
  FeaturesIcon,
  FeaturesValue,
  Heading,
  LinkButton,
  LinkButtonsContainer,
  LinkButtonText,
  ProfileButtonText
} from "../StudentProfileChatBox/styles";
import { Calendar2, HouseDoor } from "@styled-icons/bootstrap";
import { useTranslation } from "react-i18next";
import { formatPreferredDate } from "../StudentProfileChatBox";
import { calculateHousePrice } from "../utils";
import { differenceInDays } from "date-fns";
import { parseServerDateTime } from "../../../utils/time";
import { MatchStatusEnum, ROOM_INFO_ROUTE_ID } from "../../../utils/constants";
import { BuildingIcon } from "../../Icons/Building";
import { StudentChatRoomStatus } from "./StudentChatRoomStatus";
import { Button as AntdButton } from "antd";
import { DefaultFont } from "../../../styles/fonts";
import { generatePath, useNavigate, NavLink } from "react-router-dom";

const Root = styled.div`
  padding: 32px 24px;
`;

const RoomPhotosCarousel = styled(RoomPhotosCarouselOriginal)`
  border-radius: 8px;
  overflow: hidden;
`;

const PriceBox = styled.div`
  padding: 8px 16px;
  display: flex;
  gap: 16px;
  background: #f3f3f3;
  border-radius: 8px;
`;

const PriceBoxEntity = styled.div``;
const PriceBoxLabel = styled.div`
  margin-bottom: 4px;
  color: ${(p) => p.theme.colors.chatFadedTextColor};
`;
const PriceBoxValue = styled.div``;
const PriceBoxPlus = styled.div`
  align-self: end;
`;
const Bold = styled.span`
  font-weight: ${(p) => p.theme.font.weightMedium};
`;
export const ProfileButton = styled(AntdButton)`
  ${DefaultFont};
  font-size: 16px;
  color: ${(p) => p.theme.colors.chatTextColor};
  display: flex;
  align-items: center;
  width: 100%;
  padding-left: 0;
  padding-right: 0;
  margin-top: 16px;

  &:hover {
    background: none !important;
  }
`;
export const RoomProfileChatBox = React.memo<{
  house: IHouse;
  match: IMatch;
}>(({ house, match }) => {
  const { t, i18n } = useTranslation("hostrooms");
  const navigate = useNavigate();
  const startDate = match.offered_check_in_date ?? match.check_in_date;
  const endDate = match.offered_check_out_date ?? match.check_out_date;
  const duration =
    startDate && endDate
      ? differenceInDays(
          parseServerDateTime(endDate),
          parseServerDateTime(startDate)
        )
      : null;
  const rentPrice = duration ? (
    <div>
      Total rent <Bold>€{calculateHousePrice(house)}</Bold> -p.m
    </div>
  ) : null;

  const studentHasProposal = [
    MatchStatusEnum.INVITED,
    MatchStatusEnum.ACCEPTED,
    MatchStatusEnum.PENDING,
    MatchStatusEnum.ACTIVE,
    MatchStatusEnum.ARCHIVED
  ].includes(match.match_status!);

  return (
    <Root>
      <div
        style={{
          marginBottom: "16px"
        }}
      >
        {house.photos && house.photos.length > 0 ? (
          <RoomPhotosCarousel
            images={house.photos.map((p) => {
              return p.url;
            })}
          />
        ) : (
          <PhotoPlaceholder>{t(`roomsDetails.noPhoto`)}</PhotoPlaceholder>
        )}
      </div>
      <Heading>
        <NavLink
          to={generatePath(ROOM_INFO_ROUTE_ID, {
            lng: i18n.language,
            id: house.house_id ?? ""
          })}
          style={{ color: "black" }}
        >
          {house.title}
        </NavLink>
      </Heading>
      <FeaturesBlock>
        <FeaturesIcon>
          <BuildingIcon />
        </FeaturesIcon>
        <FeaturesValue>{house.city}</FeaturesValue>
      </FeaturesBlock>
      <FeaturesBlock>
        <FeaturesIcon>
          <HouseDoor />
        </FeaturesIcon>
        <FeaturesValue>
          {t(`roomsDetails.typeRoom.items.${house.kind}.title`)}
        </FeaturesValue>
      </FeaturesBlock>
      <FeaturesBlock>
        <FeaturesIcon>
          <Calendar2 />
        </FeaturesIcon>
        <FeaturesValue>
          {t(`roomsDetails.preferredDate`)}
          <div>
            {formatPreferredDate(house.available_from)} -{" "}
            {formatPreferredDate(house.available_to)}
          </div>
        </FeaturesValue>
      </FeaturesBlock>
      <PriceBox>
        <PriceBoxEntity>
          <PriceBoxLabel>{t(`roomsDetails.rent`)}</PriceBoxLabel>
          <PriceBoxValue>
            <Bold>€{house.price},</Bold>- p.m
          </PriceBoxValue>
        </PriceBoxEntity>
        <PriceBoxPlus>
          <Bold>+</Bold>
        </PriceBoxPlus>
        <PriceBoxEntity>
          <PriceBoxLabel>{t(`roomsDetails.utilities`)}</PriceBoxLabel>
          <PriceBoxValue>
            <Bold>€{house.additional_expenses},</Bold>- p.m
          </PriceBoxValue>
        </PriceBoxEntity>
      </PriceBox>
      <ProfileButton
        type="text"
        onClick={() => {
          navigate(
            generatePath(ROOM_INFO_ROUTE_ID, {
              lng: i18n.language,
              id: house.house_id ?? ""
            })
          );
        }}
      >
        <ProfileButtonText>{t(`roomsDetails.showMore`)}</ProfileButtonText>
        <Chevron />
      </ProfileButton>
      <Delimiter />
      <Heading>{t(`roomsDetails.reservationStatus`)} </Heading>
      {studentHasProposal ? (
        <FeaturesBlock>
          <FeaturesIcon>
            <Calendar2 />
          </FeaturesIcon>

          <FeaturesValue>
            {t(`roomsDetails.proposedDates`)}
            <div>
              {formatPreferredDate(startDate)} - {formatPreferredDate(endDate)}
            </div>
          </FeaturesValue>
        </FeaturesBlock>
      ) : null}
      {studentHasProposal ? (
        <PriceBox style={{ marginTop: "16px" }}>{rentPrice}</PriceBox>
      ) : null}
      <div style={{ marginTop: "16px" }} />
      <StudentChatRoomStatus match={match} />
      <Delimiter />
      <Heading>{t(`roomsDetails.tipsAndTricks`)}</Heading>
      <LinkButtonsContainer>
        <LinkButton
          type="text"
          href={`/${i18n.language}/blog/find-a-room-in-the-Netherlands`}
          target={"_blank"}
        >
          <LinkButtonText>{t(`roomsDetails.improveProfile`)} </LinkButtonText>
          <Chevron />
        </LinkButton>
        <LinkButton
          type="text"
          href={`/${i18n.language}/blog/student-ervaring-wonen-bij-een-host-of-gastgezin`}
          target={"_blank"}
        >
          <LinkButtonText>{t(`roomsDetails.experiences`)}</LinkButtonText>
          <Chevron />
        </LinkButton>
      </LinkButtonsContainer>
      <Delimiter />
      <Heading>{t(`roomsDetails.help`)}</Heading>
      <LinkButtonsContainer>
        <LinkButton
          type="text"
          href={`/${i18n.language}/contact`}
          target={"_blank"}
        >
          <LinkButtonText>{t(`roomsDetails.contactHospi`)}</LinkButtonText>
          <Chevron />
        </LinkButton>
      </LinkButtonsContainer>
    </Root>
  );
});
